import { supabase } from '@/integrations/supabase/client'

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Sign out and back in, then try again.'

/**
 * Calls the send-team-invitation edge function and returns its JSON body,
 * or throws an Error carrying the function's real reason.
 *
 * Since 2026-10-07 the function is JWT-gated at the gateway (verify_jwt = true),
 * so a stale access token comes back as a bare 401 before the function runs.
 * On a 401 we refresh the session once and retry. The retry is safe: every 401
 * (gateway or the function's own caller check) happens before any user is
 * created or any email is sent.
 *
 * functions.invoke() reports every non-2xx as "Edge Function returned a non-2xx
 * status code"; the real reason is in the response body on error.context.
 */
export async function invokeTeamInvitation(body: Record<string, unknown>): Promise<any> {
  let { data, error } = await supabase.functions.invoke('send-team-invitation', { body })

  if (error && (error as any).context?.status === 401) {
    const { error: refreshError } = await supabase.auth.refreshSession()
    if (refreshError) throw new Error(SESSION_EXPIRED_MESSAGE)
    ;({ data, error } = await supabase.functions.invoke('send-team-invitation', { body }))
  }

  if (error) {
    const response = (error as any).context
    if (response?.status === 401) throw new Error(SESSION_EXPIRED_MESSAGE)

    let detail = error.message
    try {
      const payload = await response?.json?.()
      if (payload?.error) detail = String(payload.error)
      else if (payload?.message) detail = String(payload.message)
    } catch {
      /* keep generic message */
    }
    throw new Error(detail)
  }

  if (data?.error) throw new Error(String(data.error))
  return data
}
